/**
 * 草稿表格的网格数学：与渲染无关的纯函数，单独放一个文件是为了能被 node --test 直接跑
 * （组件是 .tsx，测试进程剥不掉 JSX）。
 *
 * 三条规矩：
 *   - 任何写入之前先用 rectOf 放大到需要的尺寸。旧版直接 grid.map(slice) 再按下标写，
 *     粘 100 行进 24 行的网格时会摸到 undefined 而整页崩掉。
 *   - 交给服务端的网格必须稠密无洞（trimGrid 之后再经 normalizeDraft），
 *     带洞的稀疏数组序列化成 null，Go 那边解不进 string。
 *   - 上限和服务端 storage.MaxDraftRows / MaxDraftCols 一一对应，超出的行贴不进去。
 */

export type Matrix = string[][]
export type Size = { rows: number; cols: number }

export const MIN_ROWS = 24
export const MIN_COLS = 8
// 与服务端 storage.MaxDraftRows / MaxDraftCols 一致。
export const MAX_ROWS = 1000
export const MAX_COLS = 26
// 光标附近永远留出这些空格子，避免"走到边界就没格子可站"的死角。
export const PAD_ROWS = 30
export const PAD_COLS = 3

export const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))

/** 取出一块稠密矩阵：比原网格大就补空格，小就裁掉。 */
export function rectOf(cells: Matrix, rows: number, cols: number): Matrix {
  return Array.from({ length: rows }, (_, r) => Array.from({ length: cols }, (_, c) => cells[r]?.[c] ?? ''))
}

/** 按内容决定网格大小：空页用默认档，有内容再加 PAD 的余量，夹在 MAX 以内。 */
export function fit(cells: Matrix): Size {
  let usedR = 0
  let usedC = 0
  cells.forEach((row, r) => {
    if (row.some((v) => v !== '')) usedR = Math.max(usedR, r + 1)
    usedC = Math.max(usedC, row.length)
  })
  return {
    rows: clamp(usedR === 0 ? MIN_ROWS : usedR + PAD_ROWS, MIN_ROWS, MAX_ROWS),
    cols: clamp(usedC === 0 ? MIN_COLS : usedC + PAD_COLS, MIN_COLS, MAX_COLS),
  }
}

/** 存回服务端前收缩：去掉整尾空行与整尾空列（服务端 normalizeDraft 做同一件事）。 */
export function trimGrid(g: Matrix): Matrix {
  let lastRow = -1
  let lastCol = -1
  g.forEach((row, r) =>
    row.forEach((v, c) => {
      if (v !== '') {
        lastRow = Math.max(lastRow, r)
        lastCol = Math.max(lastCol, c)
      }
    }),
  )
  if (lastRow < 0) return []
  return g.slice(0, lastRow + 1).map((row) => row.slice(0, lastCol + 1))
}

/** 解析一段剪贴板文本（行 = 换行，列 = 制表符），贴进 (r0,c0) 起的位置。
    返回放大后的新网格；超出 MAX 的部分丢弃并报告数量，不静默吞掉。 */
export function pasteInto(
  cells: Matrix,
  r0: number,
  c0: number,
  text: string,
): { grid: Matrix; rows: number; cols: number; droppedRows: number; droppedCols: number } {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  while (lines.length && lines[lines.length - 1] === '') lines.pop()
  const rowsIn = lines.map((l) => l.split('\t'))
  const widest = rowsIn.reduce((m, r) => Math.max(m, r.length), 0)
  const grid = rectOf(
    cells,
    Math.min(MAX_ROWS, Math.max(cells.length, r0 + rowsIn.length)),
    Math.min(MAX_COLS, Math.max(cells[0]?.length ?? 0, c0 + widest)),
  )
  let droppedRows = 0
  let droppedCols = 0
  rowsIn.forEach((row, dr) => {
    const r = r0 + dr
    if (r >= grid.length) {
      droppedRows++
      return
    }
    row.forEach((v, dc) => {
      const c = c0 + dc
      if (c >= grid[0].length) {
        droppedCols++
        return
      }
      grid[r][c] = v.trim()
    })
  })
  return { grid, rows: rowsIn.length, cols: widest, droppedRows, droppedCols }
}
